import type { SnackBarConfig } from '@shared/components/Snackbar';
import { gicsTierRank } from '@shared/domain/company/gicsTierRank';
import type { Company } from '@/scenes/authentication/types/types.auth';
import {
  Autocomplete,
  Box,
  Button,
  Card,
  CardActionArea,
  CardContent,
  Checkbox,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControlLabel,
  IconButton,
  Input,
  Tab,
  Tabs,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import React, { useEffect, useState } from 'react';
import CompanyLogo from '@shared/components/CompanyLogo';
import { useApi } from '@shared/services/api';
import CompanyDomainsPanel from '@shared/components/CompanyDomains/CompanyDomainsPanel';

interface GICSOption {
  code: string;
  name: string;
  tier?: string;
}

interface AdminEditCompanyProps {
  isOpen: boolean;
  setIsOpen: () => void;
  company: Company | null;
  onSave: (updatedCompany: Partial<Company>) => void;
  onDeleted?: (companyId: number) => void;
}

interface AdminEditCompanyState {
  companyName: string | undefined;
  isApproved: boolean;
  gicsCode: string;
  cik: string;
  stockTicker: string;
  url: string;
  profile: string;
  revenue: string;
  headcount: string;
  fiscalYearEndMonth: string;
  city: string;
  state: string;
  country: string;
}

interface CoresignalCompanyPreview {
  id: number;
  name: string | null;
  ticker: string | null;
  description: string | null;
  websitesMain: string | null;
  websitesLinkedin: string | null;
  locationHqCountry: string | null;
  employeeCount: number;
  followers: number;
}

const AdminEditCompany: React.FC<AdminEditCompanyProps> = ({
  isOpen,
  setIsOpen,
  company,
  onSave,
  onDeleted,
}) => {
  const [activeTab, setActiveTab] = useState(0);
  const [formData, setFormData] = useState<AdminEditCompanyState>({
    companyName: undefined,
    isApproved: false,
    gicsCode: '',
    cik: '',
    stockTicker: '',
    url: '',
    profile: '',
    revenue: '',
    headcount: '',
    fiscalYearEndMonth: '',
    city: '',
    state: '',
    country: '',
  });

  const [gicsOptions, setGicsOptions] = useState<GICSOption[]>([]);
  const [isLoadingGics, setIsLoadingGics] = useState<boolean>(false);
  const [isLoadingSuggestions, setIsLoadingSuggestions] = useState<boolean>(false);
  const [_snackbarConfig, setSnackbarConfig] = useState<SnackBarConfig>();

  // Coresignal state
  const [coresignalExternalId, setCoresignalExternalId] = useState<string | null>(null);
  const [coresignalMetadata, setCoresignalMetadata] = useState<Record<string, unknown> | null>(null);
  const [coresignalInput, setCoresignalInput] = useState<string>('');
  const [coresignalSearchResults, setCoresignalSearchResults] = useState<CoresignalCompanyPreview[]>([]);
  const [selectedCoresignalResult, setSelectedCoresignalResult] = useState<number | null>(null);
  const [isSearchingCoresignal, setIsSearchingCoresignal] = useState(false);
  const [isSettingCoresignalId, setIsSettingCoresignalId] = useState(false);

  // Danger tab state
  const [isDeleteConfirmOpen, setIsDeleteConfirmOpen] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);

  const api = useApi();

  // Fetch GICS options only when modal opens and options are not already loaded
  useEffect(() => {
    if (!isOpen || gicsOptions.length > 0 || isLoadingGics) {
      return;
    }

    const fetchGicsOptions = async (): Promise<void> => {
      setIsLoadingGics(true);
      try {
        const response = await api.get('/gics');
        if (response && response.data) {
          setGicsOptions(response.data);
        }
      }
      catch (error) {
        console.error('Error fetching GICS options:', error);
        setSnackbarConfig({
          message: 'Error loading GICS options',
          open: true,
          type: 'error',
        });
      }
      finally {
        setIsLoadingGics(false);
      }
    };

    fetchGicsOptions();
  }, [isOpen]);

  useEffect(() => {
    if (company) {
      setFormData({
        companyName: company.companyName || '',
        isApproved: company.isApproved || false,
        gicsCode: company.gicsCode || '',
        cik: company.cik || '',
        stockTicker: company.stockTicker || '',
        url: company.url || '',
        profile: company.profile || '',
        revenue: company.revenue || '',
        headcount: company.headcount?.toString() || '',
        fiscalYearEndMonth: company.fiscalYearEndMonth?.toString() || '',
        city: company.city || '',
        state: company.state || '',
        country: company.country || '',
      });
      // Reset Coresignal state when company changes
      setCoresignalExternalId(null);
      setCoresignalMetadata(null);
      setCoresignalInput('');
      setCoresignalSearchResults([]);
      setSelectedCoresignalResult(null);
      setActiveTab(0);
    }
  }, [company]);

  // Fetch current Coresignal ID when modal opens
  useEffect(() => {
    if (!isOpen || !company?.id) return;

    const fetchCoresignalId = async (): Promise<void> => {
      try {
        const response = await api.get(`/admin/company/${company.id}/coresignal-id`);
        const data = response?.data || response;
        if (data) {
          setCoresignalExternalId(data.externalId ?? null);
          setCoresignalMetadata(data.metadata ?? null);
        }
      }
      catch (error) {
        console.error('Error fetching Coresignal ID:', error);
      }
    };

    fetchCoresignalId();
  }, [isOpen, company?.id]);

  const handleInputChange = (field: string, value: string | boolean): void => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleLogoChange = async (event: React.ChangeEvent<HTMLInputElement>): Promise<void> => {
    const file = event.target.files?.[0];
    if (file) {
      const effectiveCompanyId = company?.id ?? '0';
      try {
        const formData = new FormData();
        formData.append('file', file);
        await api.post(`/admin/company/${effectiveCompanyId}/logo`, formData, {
          headers: {
            'Content-Type': 'multipart/form-data',
          },
        });
        setSnackbarConfig({
          message: 'Logo successfully uploaded!',
          open: true,
          type: 'success',
        });
      }
      catch (error) {
        console.error('Error uploading logo:', error);
        setSnackbarConfig({
          message: 'Error uploading logo',
          open: true,
          type: 'error',
        });
      }
    }
  };

  const handleGetSuggestions = async (): Promise<void> => {
    if (!company?.id)
      return;

    setIsLoadingSuggestions(true);
    try {
      const emptyFields: string[] = [];

      const fieldMapping: Record<string, string> = {
        cik: 'cik',
        gicsCode: 'gicsCode',
        url: 'domain',
        profile: 'description',
        revenue: 'revenue',
        headcount: 'headcount',
        fiscalYearEndMonth: 'fiscalYear',
      };

      for (const [formField, apiField] of Object.entries(fieldMapping)) {
        if (!formData[formField as keyof AdminEditCompanyState]) {
          emptyFields.push(apiField);
        }
      }

      if (!formData.city || !formData.country) {
        emptyFields.push('headquarters');
      }

      if (emptyFields.length === 0) {
        setSnackbarConfig({
          message: 'All fields are already filled',
          open: true,
          type: 'info',
        });
        return;
      }

      const response = await api.post('/ai/get-company-metadata-suggestions', {
        companyId: company.id,
        fields: emptyFields,
      });

      if (response.error) {
        throw new Error(response.message || 'Failed to get suggestions');
      }

      const suggestions = response.data || response;
      const updates: Partial<AdminEditCompanyState> = {};

      if (suggestions.cikSuggestion?.cik) {
        updates.cik = suggestions.cikSuggestion.cik;
      }

      if (suggestions.gicsCodeSuggestion?.code) {
        updates.gicsCode = suggestions.gicsCodeSuggestion.code;
      }

      if (suggestions.domainSuggestion) {
        updates.url = suggestions.domainSuggestion;
      }

      if (suggestions.revenueSuggestion !== undefined && suggestions.revenueSuggestion !== null) {
        updates.revenue = suggestions.revenueSuggestion.toString();
      }

      if (suggestions.headcountSuggestion !== undefined && suggestions.headcountSuggestion !== null) {
        updates.headcount = suggestions.headcountSuggestion.toString();
      }

      if (suggestions.fiscalYearEndMonthSuggestion !== undefined && suggestions.fiscalYearEndMonthSuggestion !== null) {
        updates.fiscalYearEndMonth = suggestions.fiscalYearEndMonthSuggestion.toString();
      }

      if (suggestions.descriptionSuggestion) {
        updates.profile = suggestions.descriptionSuggestion;
      }

      if (suggestions.headquartersSuggestion) {
        const hq = suggestions.headquartersSuggestion;
        if (hq.city) {
          updates.city = hq.city;
        }
        if (hq.state) {
          updates.state = hq.state;
        }
        if (hq.country) {
          updates.country = hq.country;
        }
      }

      setFormData(prev => ({ ...prev, ...updates }));

      const appliedCount = Object.keys(updates).length;
      setSnackbarConfig({
        message: `Applied ${appliedCount} suggestion${appliedCount !== 1 ? 's' : ''}`,
        open: true,
        type: 'success',
      });
    }
    catch (error) {
      console.error('Error getting suggestions:', error);
      setSnackbarConfig({
        message: 'Error getting AI suggestions',
        open: true,
        type: 'error',
      });
    }
    finally {
      setIsLoadingSuggestions(false);
    }
  };

  const handleSave = async (): Promise<void> => {
    if (!company)
      return;

    const updatedCompany = {
      id: company.id,
      companyName: formData.companyName,
      isApproved: formData.isApproved,
      gicsCode: formData.gicsCode === '' ? undefined : formData.gicsCode,
      cik: formData.cik === '' ? undefined : formData.cik,
      stockTicker: formData.stockTicker === '' ? undefined : formData.stockTicker,
      url: formData.url === '' ? undefined : formData.url,
      profile: formData.profile === '' ? undefined : formData.profile,
      revenue: formData.revenue === '' ? undefined : formData.revenue,
      headcount: formData.headcount === '' ? undefined : Number.parseInt(formData.headcount),
      fiscalYearEndMonth: formData.fiscalYearEndMonth === '' ? undefined : Number.parseInt(formData.fiscalYearEndMonth),
      city: formData.city === '' ? undefined : formData.city,
      state: formData.state === '' ? undefined : formData.state,
      country: formData.country === '' ? undefined : formData.country,
    };

    onSave(updatedCompany);
  };

  // ── Coresignal handlers ─────────────────────────────────────────────────

  const handleCoresignalSearch = async (): Promise<void> => {
    if (!company?.id) return;

    setIsSearchingCoresignal(true);
    setCoresignalSearchResults([]);
    setSelectedCoresignalResult(null);

    try {
      const response = await api.post(`/admin/company/${company.id}/coresignal-search`, {
        companyDomain: formData.url ? new URL(formData.url.startsWith('http') ? formData.url : `https://${formData.url}`).hostname.replace(/^www\./, '') : undefined,
        companyName: formData.companyName,
        country: formData.country || undefined,
        stockTicker: formData.stockTicker || undefined,
      });

      const data = response?.data || response;
      if (data?.results?.length > 0) {
        setCoresignalSearchResults(data.results);
      }
      else {
        setSnackbarConfig({ message: 'No Coresignal results found', open: true, type: 'info' });
      }
    }
    catch (error) {
      console.error('Error searching Coresignal:', error);
      setSnackbarConfig({ message: 'Error searching Coresignal', open: true, type: 'error' });
    }
    finally {
      setIsSearchingCoresignal(false);
    }
  };

  const handleSetCoresignalId = async (idToSet: number | null): Promise<void> => {
    if (!company?.id) return;

    setIsSettingCoresignalId(true);
    try {
      const response = await api.put(`/admin/company/${company.id}/coresignal-id`, {
        coresignalId: idToSet,
      });

      const data = response?.data || response;
      if (data) {
        setCoresignalExternalId(data.externalId ?? null);
        setCoresignalMetadata(data.metadata ?? null);
        setCoresignalInput('');
        setCoresignalSearchResults([]);
        setSelectedCoresignalResult(null);
        setSnackbarConfig({
          message: idToSet ? `Coresignal ID set to ${idToSet}` : 'Coresignal ID cleared',
          open: true,
          type: 'success',
        });
      }
    }
    catch (error) {
      console.error('Error setting Coresignal ID:', error);
      setSnackbarConfig({ message: 'Error setting Coresignal ID', open: true, type: 'error' });
    }
    finally {
      setIsSettingCoresignalId(false);
    }
  };

  // ── Danger handlers ─────────────────────────────────────────────────────

  const handleDeleteCompany = async (): Promise<void> => {
    if (!company?.id) return;

    setIsDeleting(true);
    try {
      await api.delete(`/admin/company/${company.id}`);
      setIsDeleteConfirmOpen(false);
      onDeleted?.(company.id);
      setIsOpen();
    }
    catch (error) {
      console.error('Error deleting company:', error);
      setSnackbarConfig({ message: 'Error deleting company', open: true, type: 'error' });
    }
    finally {
      setIsDeleting(false);
    }
  };

  // ── Render ──────────────────────────────────────────────────────────────

  const renderCompanyDetailsTab = (): React.ReactNode => (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <TextField
          label="Company Name"
          value={formData.companyName}
          onChange={e => handleInputChange('companyName', e.target.value)}
          fullWidth
        />
        <Tooltip title="Get AI suggestions for empty fields">
          <IconButton
            onClick={handleGetSuggestions}
            disabled={isLoadingSuggestions || !company?.id}
            color="primary"
          >
            {isLoadingSuggestions ? <CircularProgress size={20} /> : '🪄'}
          </IconButton>
        </Tooltip>
      </Box>

      <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2 }}>
        <TextField
          label="CIK (SEC Central Index Key)"
          value={formData.cik}
          onChange={e => handleInputChange('cik', e.target.value)}
          placeholder="e.g., 0000320193"
          helperText="Used to fetch SEC filings (10-Q, 10-K)"
        />

        <TextField
          label="Stock Ticker"
          value={formData.stockTicker}
          onChange={e => handleInputChange('stockTicker', e.target.value)}
          placeholder="e.g., AAPL"
          helperText="Company stock ticker symbol"
        />
      </Box>

      <TextField
        label="Company URL"
        value={formData.url}
        onChange={e => handleInputChange('url', e.target.value)}
        fullWidth
        placeholder="https://example.com"
        helperText="Company website URL"
      />

      <TextField
        label="Company Description"
        value={formData.profile}
        onChange={e => handleInputChange('profile', e.target.value)}
        fullWidth
        multiline
        rows={3}
        placeholder="Brief description of what the company does"
        helperText="3-5 sentences describing the company's business"
      />

      <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2 }}>
        <TextField
          label="Revenue"
          value={formData.revenue}
          onChange={e => handleInputChange('revenue', e.target.value)}
          placeholder="e.g., 1500000000 for $1.5B"
          helperText="Annual revenue in USD"
        />

        <TextField
          label="Headcount"
          value={formData.headcount}
          onChange={e => handleInputChange('headcount', e.target.value)}
          type="number"
          placeholder="e.g., 5000"
          helperText="Number of employees"
        />
      </Box>

      <TextField
        label="Fiscal Year End Month"
        value={formData.fiscalYearEndMonth}
        onChange={e => handleInputChange('fiscalYearEndMonth', e.target.value)}
        type="number"
        placeholder="e.g., 12 for December"
        helperText="Month number (1-12) when the fiscal year ends"
        inputProps={{ min: 1, max: 12 }}
      />

      <Box sx={{ display: 'grid', gridTemplateColumns: '2fr 1fr 2fr', gap: 2 }}>
        <TextField
          label="City"
          value={formData.city}
          onChange={e => handleInputChange('city', e.target.value)}
          placeholder="e.g., San Francisco"
          helperText="HQ city"
        />

        <TextField
          label="State"
          value={formData.state}
          onChange={e => handleInputChange('state', e.target.value)}
          placeholder="e.g., California"
          helperText="HQ State/province"
        />

        <TextField
          label="Country"
          value={formData.country}
          onChange={e => handleInputChange('country', e.target.value)}
          placeholder="e.g., United States"
          helperText="HQ country"
        />
      </Box>

      <FormControlLabel
        control={(
          <Checkbox
            checked={formData.isApproved}
            onChange={e => handleInputChange('isApproved', e.target.checked)}
          />
        )}
        label="Approved"
      />

      <Autocomplete
        options={
          gicsOptions.sort((a, b) => {
            if (a.tier === b.tier) {
              return a.name.localeCompare(b.name);
            }
            return gicsTierRank(a.tier) - gicsTierRank(b.tier);
          })
        }
        loading={isLoadingGics}
        value={gicsOptions.find(option => option.code === formData.gicsCode) || null}
        onChange={(_event, newValue) => {
          handleInputChange('gicsCode', newValue?.code || '');
        }}
        getOptionLabel={option => option.name}
        isOptionEqualToValue={(option, value) => option.code === value.code}
        groupBy={option => option.tier || 'Other'}
        renderGroup={params => (
          <li key={params.key}>
            <Typography
              variant="subtitle2"
              sx={{ backgroundColor: '#f0f0f0', padding: '4px 8px' }}
            >
              {params.group}
            </Typography>
            <ul style={{ margin: 0, padding: 0 }}>{params.children}</ul>
          </li>
        )}
        renderOption={(props, option) => (
          <Box component="li" {...props} key={option.code}>
            <Box>
              <Typography variant="body1">
                {option.name}
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.8rem' }}>
                Code:
                {' '}
                {option.code}
              </Typography>
            </Box>
          </Box>
        )}
        renderInput={params => (
          <TextField
            {...params}
            label="GICS Classification"
            fullWidth
            InputProps={params.InputProps as any}
          />
        )}
      />

      <Box>
        <Typography variant="subtitle2" gutterBottom>
          Company Logo
        </Typography>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <CompanyLogo
            companyName={company?.companyName || 'Company'}
            companyId={company?.id}
            companyUrl={company?.url ?? undefined}
            sx={{ width: 64, height: 64 }}
            variant="square"
          />
          <Input
            type="file"
            onChange={handleLogoChange}
            inputProps={{ 'aria-label': 'Upload logo' }}
          />
        </Box>
      </Box>
    </Box>
  );

  const renderCoresignalPreviewCard = (preview: CoresignalCompanyPreview): React.ReactNode => {
    const isSelected = selectedCoresignalResult === preview.id;
    const isCurrent = coresignalExternalId === String(preview.id);

    return (
      <Card
        key={preview.id}
        variant="outlined"
        sx={{
          border: isSelected ? '2px solid' : '1px solid',
          borderColor: isSelected ? 'primary.main' : 'divider',
          backgroundColor: isCurrent ? 'action.selected' : undefined,
        }}
      >
        <CardActionArea onClick={() => setSelectedCoresignalResult(preview.id)}>
          <CardContent sx={{ py: 1.5, '&:last-child': { pb: 1.5 } }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 0.5 }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                {preview.name || 'Unknown'}
                {' '}
                <Typography component="span" variant="body2" color="text.secondary">
                  (ID: {preview.id})
                </Typography>
              </Typography>
              <Box sx={{ display: 'flex', gap: 0.5 }}>
                {isCurrent && <Chip label="Current" size="small" color="success" />}
                {isSelected && <Chip label="Selected" size="small" color="primary" />}
              </Box>
            </Box>

            <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', mb: 0.5 }}>
              {preview.websitesMain && (
                <Typography variant="body2" color="text.secondary">
                  {preview.websitesMain}
                </Typography>
              )}
              {preview.websitesLinkedin && (
                <Typography variant="body2" color="text.secondary">
                  {preview.websitesLinkedin}
                </Typography>
              )}
            </Box>

            <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', mb: 0.5 }}>
              {preview.locationHqCountry && (
                <Typography variant="body2">{preview.locationHqCountry}</Typography>
              )}
              {preview.ticker && (
                <Typography variant="body2">Ticker: {preview.ticker}</Typography>
              )}
              <Typography variant="body2">{preview.employeeCount} employees</Typography>
              <Typography variant="body2">{preview.followers} followers</Typography>
            </Box>

            {preview.description && (
              <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }} noWrap>
                {preview.description}
              </Typography>
            )}
          </CardContent>
        </CardActionArea>
      </Card>
    );
  };

  const renderCoresignalTab = (): React.ReactNode => (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
      <Typography variant="h6">Coresignal Company ID</Typography>

      {/* Current ID + stored metadata */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <Typography variant="body2" color="text.secondary">
          Current ID:
        </Typography>
        {coresignalExternalId
          ? (
            <>
              <Chip label={coresignalExternalId} size="small" />
              <Button
                size="small"
                color="error"
                variant="text"
                onClick={() => handleSetCoresignalId(null)}
                disabled={isSettingCoresignalId}
              >
                Clear
              </Button>
            </>
          )
          : (
            <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>
              Not set
            </Typography>
          )}
      </Box>

      {coresignalExternalId && coresignalMetadata && (
        <Card variant="outlined" sx={{ backgroundColor: 'grey.50' }}>
          <CardContent sx={{ py: 1.5, '&:last-child': { pb: 1.5 } }}>
            <Typography variant="subtitle2" sx={{ mb: 1 }}>Stored Coresignal Data</Typography>
            <Box sx={{ display: 'grid', gridTemplateColumns: '140px 1fr', rowGap: 0.5, columnGap: 2 }}>
              <Typography variant="body2" color="text.secondary">Website:</Typography>
              <Typography variant="body2">{(coresignalMetadata.websites_main as string) || 'N/A'}</Typography>

              <Typography variant="body2" color="text.secondary">LinkedIn:</Typography>
              <Typography variant="body2" sx={{ wordBreak: 'break-all' }}>
                {(coresignalMetadata.websites_linkedin as string) || 'N/A'}
              </Typography>

              <Typography variant="body2" color="text.secondary">HQ Country:</Typography>
              <Typography variant="body2">{(coresignalMetadata.location_hq_country as string) || 'N/A'}</Typography>

              <Typography variant="body2" color="text.secondary">Description:</Typography>
              <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                {(coresignalMetadata.description as string) || 'N/A'}
              </Typography>
            </Box>
          </CardContent>
        </Card>
      )}

      <Divider />

      {/* Manual ID entry or search */}
      <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
        <TextField
          label="Enter Coresignal ID manually"
          value={coresignalInput}
          onChange={e => setCoresignalInput(e.target.value)}
          type="number"
          size="small"
          sx={{ flex: 1 }}
        />
        <Button
          variant="outlined"
          onClick={() => handleSetCoresignalId(Number(coresignalInput))}
          disabled={!coresignalInput || isSettingCoresignalId}
        >
          {isSettingCoresignalId ? <CircularProgress size={20} /> : 'Set ID'}
        </Button>
        <Typography variant="body2" color="text.secondary">or</Typography>
        <Button
          variant="outlined"
          onClick={handleCoresignalSearch}
          disabled={isSearchingCoresignal || !formData.companyName}
        >
          {isSearchingCoresignal ? <CircularProgress size={20} /> : 'Search Coresignal'}
        </Button>
      </Box>

      {/* Search results */}
      {coresignalSearchResults.length > 0 && (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
          <Typography variant="subtitle2">
            Search Results ({coresignalSearchResults.length})
          </Typography>

          {coresignalSearchResults.map(preview => renderCoresignalPreviewCard(preview))}

          <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 1 }}>
            <Button
              variant="contained"
              onClick={() => handleSetCoresignalId(selectedCoresignalResult)}
              disabled={selectedCoresignalResult == null || isSettingCoresignalId}
            >
              {isSettingCoresignalId ? <CircularProgress size={20} /> : 'Set Selected ID'}
            </Button>
          </Box>
        </Box>
      )}
    </Box>
  );

  const renderDangerTab = (): React.ReactNode => (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
      <Typography variant="h6" color="error">Danger Zone</Typography>
      <Typography variant="body2" color="text.secondary">
        Deleting a company is a soft delete — the record is hidden but not permanently removed.
      </Typography>
      <Box>
        <Button
          variant="outlined"
          color="error"
          onClick={() => setIsDeleteConfirmOpen(true)}
          disabled={isDeleting || !company?.id}
        >
          Delete Company
        </Button>
      </Box>
    </Box>
  );

  return (
    <Dialog open={isOpen} onClose={setIsOpen} maxWidth="lg" fullWidth>
      <DialogTitle>Edit Company</DialogTitle>

      <Tabs
        value={activeTab}
        onChange={(_e, newValue) => setActiveTab(newValue)}
        sx={{ borderBottom: 1, borderColor: 'divider', px: 3 }}
        TabIndicatorProps={{
          sx: { backgroundColor: activeTab === 3 ? 'error.main' : undefined },
        }}
      >
        <Tab label="Company Details" />
        <Tab label="Domains" disabled={!company?.id} />
        <Tab label="Coresignal" disabled={!company?.id} />
        <Tab
          label="Danger"
          disabled={!company?.id}
          sx={{
            marginLeft: 'auto',
            color: 'error.main',
            '&.Mui-selected': { color: 'error.main' },
          }}
        />
      </Tabs>

      <DialogContent sx={{ minHeight: 400 }}>
        {activeTab === 0 && renderCompanyDetailsTab()}
        {activeTab === 1 && company?.id && (
          <Box sx={{ mt: 1 }}>
            <CompanyDomainsPanel companyId={company.id} />
          </Box>
        )}
        {activeTab === 2 && renderCoresignalTab()}
        {activeTab === 3 && renderDangerTab()}
      </DialogContent>

      <DialogActions>
        <Button onClick={setIsOpen}>Cancel</Button>
        <Button onClick={handleSave} variant="contained">
          Save Changes
        </Button>
      </DialogActions>

      <Dialog
        open={isDeleteConfirmOpen}
        onClose={() => !isDeleting && setIsDeleteConfirmOpen(false)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>Delete Company?</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete
            {' '}
            <strong>{company?.companyName || 'this company'}</strong>
            ? This will soft-delete the company record.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setIsDeleteConfirmOpen(false)}
            disabled={isDeleting}
          >
            Cancel
          </Button>
          <Button
            color="error"
            variant="contained"
            onClick={handleDeleteCompany}
            disabled={isDeleting}
          >
            {isDeleting ? <CircularProgress size={20} /> : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>
    </Dialog>
  );
};

export default AdminEditCompany;
